import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'receive'
  | 'dispatch'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-sm',
  receive: 'bg-receive-600 text-white hover:bg-receive-700 active:bg-receive-700 shadow-sm',
  dispatch: 'bg-dispatch-600 text-white hover:bg-dispatch-700 active:bg-dispatch-700 shadow-sm',
  secondary: 'bg-white text-ink border border-line hover:bg-canvas active:bg-line',
  ghost: 'bg-transparent text-ink-soft hover:bg-canvas hover:text-ink',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
  link: 'bg-transparent text-brand-600 hover:text-brand-700 hover:underline underline-offset-4 px-0',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-5 text-[15px] gap-2 rounded-xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors',
        'disabled:pointer-events-none disabled:opacity-55',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}

/** Same look as `Button`, but navigates instead of firing a handler. */
export function LinkButton({
  to,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  children,
}: {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </Link>
  );
}

/** Square icon-only button — used in table rows and toolbars. */
export function IconButton({
  label,
  variant = 'ghost',
  size = 'md',
  className,
  children,
  ...rest
}: Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'fullWidth'> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-lg transition-colors',
        'disabled:pointer-events-none disabled:opacity-55',
        VARIANTS[variant],
        size === 'sm' ? 'size-8' : size === 'lg' ? 'size-11' : 'size-9',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
